import { getMapMarkerImageWidth, MAP_MARKER_HEIGHT } from './mapMarkerImage';
import type { buildMapMarkerFeatureCollection } from './mapMarkerLayer';

// Leave room for the largest dot halo (7.4px) and a small gap around each price.
const DOT_CLEARANCE = 8;

type MarkerDensityOptions = {
  /** Marker features whose image IDs have measured widths registered on this map. */
  featureCollection: ReturnType<typeof buildMapMarkerFeatureCollection>;
  /** The image registry's map instance, projecting longitude/latitude to viewport CSS pixels. */
  map: { project: (coordinates: [number, number]) => { x: number; y: number } };
};

export function getMapMarkerDotOnlyIds({ featureCollection, map }: MarkerDensityOptions) {
  if (featureCollection.features.length < 2) {
    return [];
  }
  const halfHeight = MAP_MARKER_HEIGHT / 2;
  const clearanceRadius = halfHeight + DOT_CLEARANCE;
  // Half-radius cells let crowded locations collapse without scanning every pair.
  const cellSize = clearanceRadius / 2;
  const positions = featureCollection.features.flatMap(({ geometry, properties }) => {
    const [longitude, latitude] = geometry.coordinates;
    const width = getMapMarkerImageWidth({ id: properties.imageId, map });
    if (longitude == null || latitude == null || width == null) {
      return [];
    }
    const { x, y } = map.project([longitude, latitude]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return [];
    }
    return [
      {
        addressId: properties.addressId,
        width,
        x,
        y,
        cellX: Math.floor(x / cellSize),
        cellY: Math.floor(y / cellSize),
      },
    ];
  });
  const cells = new Map<string, typeof positions>();
  for (const position of positions) {
    const key = `${position.cellX},${position.cellY}`;
    const cell = cells.get(key);
    if (cell) {
      cell.push(position);
    } else {
      cells.set(key, [position]);
    }
  }
  const dotOnlyIds = new Set<string>();
  for (const position of positions) {
    if ((cells.get(`${position.cellX},${position.cellY}`)?.length ?? 0) > 1) {
      dotOnlyIds.add(position.addressId);
      continue;
    }
    const clearanceWidth = position.width / 2 + DOT_CLEARANCE;
    const minCellX = Math.floor((position.x - clearanceWidth) / cellSize);
    const maxCellX = Math.floor((position.x + clearanceWidth) / cellSize);
    const minCellY = Math.floor((position.y - clearanceRadius) / cellSize);
    const maxCellY = Math.floor((position.y + clearanceRadius) / cellSize);
    let overlapsNeighbor = false;
    for (let cellX = minCellX; cellX <= maxCellX && !overlapsNeighbor; cellX++) {
      for (let cellY = minCellY; cellY <= maxCellY && !overlapsNeighbor; cellY++) {
        if (cellX === position.cellX && cellY === position.cellY) {
          continue;
        }
        const neighbors = cells.get(`${cellX},${cellY}`);
        overlapsNeighbor =
          neighbors?.some((neighbor) => {
            const distanceX = Math.abs(neighbor.x - position.x);
            const distanceY = Math.abs(neighbor.y - position.y);
            const cornerX = Math.max(0, distanceX - (position.width / 2 - halfHeight));
            return cornerX ** 2 + distanceY ** 2 <= clearanceRadius ** 2;
          }) ?? false;
      }
    }
    if (overlapsNeighbor) {
      dotOnlyIds.add(position.addressId);
    }
  }
  const addressIds = [...dotOnlyIds];
  addressIds.sort();
  return addressIds;
}
